/**
 * Auth saga.
 *
 * Login no longer reads a JWT out of the response — the server sets it as
 * an HttpOnly cookie. The CSRF token arrives as a non-HttpOnly cookie; the
 * api/client wrapper reads it on each write request.
 *
 * On app boot we call /auth/me to discover the actual session state,
 * rather than trusting a localStorage hint that can lie about whether the
 * cookie is still valid.
 */

import { takeLatest, call, put } from 'redux-saga/effects';
import {
    bootstrapResolved,
    loginSuccess,
    loginFailure,
    logoutSuccess,
    resetPasswordSuccess,
    resetPasswordFailure,
    updatePasswordSuccess,
    updatePasswordFailure,
} from './authSlice';

const BASE = process.env.REACT_APP_SERVER_API || '';

async function callJson(url, init = {}) {
    const res = await fetch(url, { credentials: 'include', ...init });
    let body = null;
    try { body = await res.json(); } catch { /* may be empty */ }
    if (!res.ok) {
        const err = new Error(body?.message || `Request failed (${res.status})`);
        err.status = res.status;
        err.body = body;
        throw err;
    }
    return body;
}

function readCsrfCookie() {
    if (typeof document === 'undefined') return null;
    const m = document.cookie.match(/(?:^|;\s*)dwms_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
}

function* bootstrap() {
    try {
        const response = yield call(callJson, `${BASE}auth/me`);
        yield put(bootstrapResolved(response));
    } catch (error) {
        // 401 = no valid cookie; any other failure → treat as logged out too,
        // the user can re-authenticate.
        yield put(bootstrapResolved({}));
    }
}

function* login(action) {
    try {
        const response = yield call(callJson, `${BASE}auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(action.payload),
        });
        if (response?.user) response.userData = response.user;
        yield put(loginSuccess(response));
    } catch (error) {
        yield put(loginFailure(error?.body?.message || error?.message || 'Login failed'));
    }
}

function* resetPassword(action) {
    try {
        const response = yield call(callJson, `${BASE}auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(action.payload),
        });
        yield put(resetPasswordSuccess(response));
    } catch (error) {
        yield put(resetPasswordFailure(error?.body?.message || error?.message));
    }
}

function* updatePassword(action) {
    try {
        // The welcome / reset email links to /new-password/:token. The user is
        // not logged in yet (no auth cookie), so the one-time JWT from the URL
        // must travel in the Authorization header — that's where verifyToken
        // looks for it. (/auth/* is CSRF-exempt, so no CSRF header is needed.)
        const { Authorization, ...body } = action.payload;
        const headers = { 'Content-Type': 'application/json' };
        if (Authorization) headers['Authorization'] = Authorization;
        const response = yield call(callJson, `${BASE}auth/update-password`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });
        yield put(updatePasswordSuccess(response));
    } catch (error) {
        yield put(updatePasswordFailure(error?.body?.message || error?.message));
    }
}

function* logout() {
    try {
        const csrf = readCsrfCookie();
        const headers = csrf ? { 'X-CSRF-Token': csrf } : {};
        yield call(callJson, `${BASE}auth/logout`, { method: 'POST', headers });
    } catch { /* clear local state regardless */ }
    try {
        localStorage.removeItem('currentBranch');
        sessionStorage.removeItem('defaultBranch');
    } catch { /* ignore */ }
    yield put(logoutSuccess());
}

function* authSaga() {
    yield takeLatest('auth/bootstrapRequest', bootstrap);
    yield takeLatest('auth/loginRequest', login);
    yield takeLatest('auth/logoutRequest', logout);
    yield takeLatest('auth/resetPasswordRequest', resetPassword);
    yield takeLatest('auth/updatePasswordRequest', updatePassword);
}

export default authSaga;
